import { components } from '@feedfm/clips-services-api';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useStudioAuthenticatedContext } from '../../context/StudioContext';
import { CollectionSortBy } from '../../util/collectionSort';
import { Explicitness } from '../Explicitness/Explicitness';
import { LazyThumbnail } from '../LazyThumbnail';
import { SimilarTracksModal } from '../SimilarTracksModal/SimilarTracksModal';
import './ClipList.css';
import * as styles from './ClipList.styles';

type SchemaClip = components['schemas']['Clip'];

// Default create date for clips without a createDate
const DEFAULT_CREATE_DATE = '2020-01-01T00:00:00.000Z';

// Base clip interface that both API and local clips can extend
interface BaseClip {
  clipId: number;
  trackId: number;
  artworkPreSignedUrl?: string;
  artworkThumbnailUrl?: string | null;
  title: string;
  artist: string;
  album: string;
  duration: number;
  preSignedUrl: string;
  explicit?: boolean;
}

// Narrow type for API-sourced clips that carry extra metadata
type ApiClipExtras = Pick<
  SchemaClip,
  'preSignedUrlExpires' | 'encoding' | 'bitrate' | 'createDate'
>;

function isApiClipExtras(clip: BaseClip): clip is BaseClip & ApiClipExtras {
  // Trust the API contract: presence of createDate identifies API-sourced clips
  return 'createDate' in clip;
}

function convertToSchemaClip(clip: BaseClip): SchemaClip {
  const isApiClip = isApiClipExtras(clip);

  return {
    clipId: clip.clipId,
    trackId: clip.trackId,
    artworkPreSignedUrl: clip.artworkPreSignedUrl,
    artworkThumbnailUrl: clip.artworkThumbnailUrl ?? null,
    title: clip.title,
    artist: clip.artist,
    album: clip.album,
    duration: clip.duration,
    preSignedUrl: clip.preSignedUrl,
    preSignedUrlExpires: isApiClip ? clip.preSignedUrlExpires || '' : '',
    explicitness: (isApiClip && 'explicitness' in clip
      ? clip.explicitness
      : 'unknown') as SchemaClip['explicitness'],
    encoding: isApiClip ? clip.encoding || '' : '',
    bitrate: isApiClip ? clip.bitrate || 0 : 0,
    createDate: isApiClip
      ? clip.createDate || DEFAULT_CREATE_DATE
      : DEFAULT_CREATE_DATE,
  };
}

interface ClipListProps {
  clips: BaseClip[];
  onPlay?: (clip: BaseClip) => void;
  showIds?: boolean;
  variant?: 'list' | 'cards';
}

export const ClipList = ({
  clips,
  onPlay,
  showIds = false,
  variant = 'list',
}: ClipListProps) => {
  const { playerState } = useStudioAuthenticatedContext();
  const [similarTracksModal, setSimilarTracksModal] = useState<{
    open: boolean;
    clip: BaseClip | null;
  }>({ open: false, clip: null });

  const handlePlayPause = (clip: BaseClip) => {
    // Convert BaseClip to API clip shape for the global player
    const collectionClip = convertToSchemaClip(clip);

    // Create a single-clip collection to play the individual clip
    const singleClipCollection: components['schemas']['Collection'] = {
      collectionId: clip.clipId, // Use clipId as collectionId for single clips
      collectionName: clip.title,
      description: `Single clip: ${clip.title}`,
      publishedOn: null,
      rating: 'unknown',
      explicit: false,
      clips: [collectionClip],
    };

    // Use the global player to play the single-clip collection
    playerState.clickCollection(singleClipCollection, CollectionSortBy.NAME);

    // Call the optional onPlay callback
    if (onPlay) {
      onPlay(clip);
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isNew = (createDate: string): boolean => {
    const created = new Date(createDate);
    const now = new Date();
    const daysDiff =
      (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff <= 7; // Consider clips "new" if created within last 7 days
  };

  // Check if a clip is currently playing
  const isClipPlaying = (clip: BaseClip): boolean => {
    return (
      playerState.currentTrack?.clipId === clip.clipId && playerState.playIntent
    );
  };

  if (variant === 'cards') {
    return (
      <>
        <Box sx={styles.cardsContainer}>
          {/* Header row for cards layout using TableHead to match cell paddings */}
          <TableContainer>
            <Table size="small" sx={styles.table}>
              <TableBody>
                <TableRow>
                  <TableCell sx={styles.headerCellTitle}>
                    <Typography variant="caption" sx={styles.headerLabel}>
                      Title
                    </Typography>
                  </TableCell>
                  <TableCell sx={styles.headerCellAlbum}>
                    <Typography variant="caption" sx={styles.headerLabel}>
                      Album
                    </Typography>
                  </TableCell>
                  <TableCell sx={styles.headerCellDuration}>
                    <Typography variant="caption" sx={styles.headerLabel}>
                      Duration
                    </Typography>
                  </TableCell>
                  <TableCell sx={styles.headerCellActions} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          {clips.map((clip) => {
            const collectionClip = convertToSchemaClip(clip);
            return (
              <Paper key={clip.clipId} elevation={1} sx={styles.cardPaper}>
                <TableContainer>
                  <Table size="small" sx={styles.table}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={styles.cardCellTitle}>
                          <Box sx={styles.artworkContainer}>
                            <Box sx={styles.artworkImageWrapper}>
                              <LazyThumbnail
                                src={clip.artworkThumbnailUrl}
                                fallbackSrc={clip.artworkPreSignedUrl}
                                alt="Album cover"
                                sx={styles.artworkImage}
                                placeholder={
                                  <Box sx={styles.artworkPlaceholder}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      No Art
                                    </Typography>
                                  </Box>
                                }
                              />

                              <IconButton
                                size="small"
                                onClick={() => handlePlayPause(clip)}
                                sx={styles.playButton(!!isClipPlaying(clip))}
                              >
                                {isClipPlaying(clip) ? (
                                  <PauseIcon sx={styles.playPauseIcon} />
                                ) : (
                                  <PlayArrowIcon sx={styles.playPauseIcon} />
                                )}
                              </IconButton>
                            </Box>
                            <Box>
                              <Box sx={styles.titleContainer}>
                                <Typography
                                  variant="body1"
                                  color="text.primary"
                                  sx={styles.title}
                                >
                                  {clip.title}
                                </Typography>
                                {collectionClip.explicitness &&
                                  collectionClip.explicitness !== 'unknown' && (
                                    <Explicitness
                                      explicitness={collectionClip.explicitness}
                                    />
                                  )}
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {clip.artist}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={styles.cardCellAlbum}>
                          <Typography variant="body1" color="text.primary">
                            {clip.album}
                          </Typography>
                        </TableCell>

                        <TableCell sx={styles.cardCellDuration}>
                          <Typography variant="body1" color="text.primary">
                            {formatDuration(clip.duration)}
                          </Typography>
                        </TableCell>

                        <TableCell sx={styles.cardCellActions}>
                          <Box sx={styles.actionsContainer}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<SearchIcon />}
                              onClick={() =>
                                setSimilarTracksModal({ open: true, clip })
                              }
                              sx={styles.findSimilarButton}
                            >
                              Find Similar
                            </Button>
                            {showIds &&
                              'createDate' in clip &&
                              typeof clip.createDate === 'string' &&
                              isNew(clip.createDate) && (
                                <Chip
                                  label="New"
                                  size="small"
                                  variant="outlined"
                                  sx={styles.newChip}
                                />
                              )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })}
        </Box>

        {/* Similar Tracks Modal */}
        {similarTracksModal.clip && (
          <SimilarTracksModal
            open={similarTracksModal.open}
            onClose={() => setSimilarTracksModal({ open: false, clip: null })}
            trackTitle={similarTracksModal.clip.title}
            trackArtist={similarTracksModal.clip.artist}
            audioUrl={similarTracksModal.clip.preSignedUrl}
          />
        )}
      </>
    );
  }

  return (
    <>
      <List className="clip-list">
        {clips.map((clip) => (
          <ListItem key={clip.clipId} className="clip-item">
            <Box className="clip-artwork" onClick={() => handlePlayPause(clip)}>
              <LazyThumbnail
                src={clip.artworkThumbnailUrl}
                fallbackSrc={clip.artworkPreSignedUrl}
                alt={`${clip.title} artwork`}
                className="artwork-image"
                placeholder={
                  <Box className="no-artwork">
                    <Typography variant="caption" color="text.secondary">
                      No Art
                    </Typography>
                  </Box>
                }
              />

              <div className="play-button-container">
                <IconButton
                  edge="end"
                  aria-label={isClipPlaying(clip) ? 'pause' : 'play'}
                  className="play-button"
                >
                  {isClipPlaying(clip) ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
              </div>
            </Box>
            <ListItemText
              primary={clip.title}
              secondary={
                <Box className="clip-details">
                  <Typography component="span" className="clip-artist">
                    {clip.artist}
                  </Typography>
                  <Typography component="span" className="clip-album">
                    {clip.album}
                  </Typography>
                  <Typography component="span" className="clip-duration">
                    {formatDuration(clip.duration)}
                  </Typography>
                  {showIds && (
                    <>
                      <Typography component="span" className="clip-id">
                        Clip ID: {clip.clipId}
                      </Typography>
                      <Typography component="span" className="track-id">
                        Track ID: {clip.trackId}
                      </Typography>
                    </>
                  )}
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>

      {/* Similar Tracks Modal */}
      {similarTracksModal.clip && (
        <SimilarTracksModal
          open={similarTracksModal.open}
          onClose={() => setSimilarTracksModal({ open: false, clip: null })}
          trackTitle={similarTracksModal.clip.title}
          trackArtist={similarTracksModal.clip.artist}
          audioUrl={similarTracksModal.clip.preSignedUrl}
        />
      )}
    </>
  );
};
